import { createContext, useContext } from "react";
import { useGetPricingProfilesQuery } from "@/redux/apis/config/trip/tripCost";
import { useAppSelector } from "@/redux/hooks";
import { pricingProfilesSelector } from "@/redux/slices/trip/selectors";
import { PricingProfile } from "@/redux/slices/trip/types";
import { ErrorPanel } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useGetPricingProfilesParams } from "./useGetPricingProfilesParams";

interface ContractPricingProfilesState {
  profiles: PricingProfile[];
  isFetching: boolean;
}

const ContractPricingProfilesContext = createContext<ContractPricingProfilesState | null>(null);

export const ContractPricingProfilesContextProvider = ({ children }: React.PropsWithChildren) => {
  const { params } = useGetPricingProfilesParams();
  const { isFetching, error } = useGetPricingProfilesQuery(params, { refetchOnMountOrArgChange: true });
  const profiles = useAppSelector(pricingProfilesSelector);

  if (error) return (
    <div className="flex flex-1 flex-col min-h-[calc(100vh-120px)] px-5 py-4">
      <ErrorPanel messages={getErrorMessages(error)} />
    </div>
  );

  return (
    <ContractPricingProfilesContext.Provider value={{ profiles, isFetching }}>
      {children}
    </ContractPricingProfilesContext.Provider>
  );
};

export const useContractPricingProfilesContext = () => {
  const context = useContext(ContractPricingProfilesContext);
  if (!context) throw new Error("useContractPricingProfilesContext must be used within a ContractPricingProfilesContextProvider");

  return context;
};
